import {
  AgeSegmentationIds,
  AllAllergySegmentationIds,
  CatNutritionSegmentationIds,
  isAllergySegmentation,
  isCatAllergySegmentation,
  isCatSegmentation,
  isNutritionSegmentation,
  NutritionSegmentationIds,
} from 'constants/segmentation';
import { SegmentationStore } from 'contexts/segmentation';
import useDetectChanges from 'hooks/common/use-detect-changes';
import { useEffect, useRef } from 'react';
import { usePetProfilesStore } from 'stores/pet-profiles';
import { useUserSegmentationStore } from 'stores/user-segmentation';
import { isCatProfile, PetProfile } from 'types/pet-profile';

type AllSegmentationTypes =
  | NutritionSegmentationIds
  | CatNutritionSegmentationIds
  | AgeSegmentationIds
  | AllAllergySegmentationIds;

interface UseSyncSegmentationProps {
  petProfile: PetProfile;
  multipleSegmentation: SegmentationStore | undefined;
  pushSegmentation: (arg: {
    segmentation: string | SegmentationStore;
    isUserAction?: boolean;
  }) => void;
  removeSegmentation: (arg: { segmentation: string }) => void;
}

/**
 * Hook to sync pet profile segments with segmentation context
 * Handles pet type switching by detecting activeType changes
 */
const useSyncSegmentation = ({
  petProfile,
  multipleSegmentation,
  pushSegmentation,
  removeSegmentation,
}: UseSyncSegmentationProps): void => {
  const previousSegmentsRef = useRef<AllSegmentationTypes[]>([]);
  const previousTypeRef = useRef<string>(petProfile.petType);

  // Get activeType from pet profiles store to detect type switches
  const activeType = usePetProfilesStore((state) => state.activeType);
  const { removeSegment } = useUserSegmentationStore();
  const userSegments = useUserSegmentationStore((state) => state.segments);

  const changes = useDetectChanges(petProfile);
  const changesRef = useRef<string | null>(null);

  useEffect(() => {
    // Try catch to prevent errors by Json.stringify
    try {
      const currentJson = JSON.stringify(changes);
      if (changes && currentJson === changesRef.current) return;
      changesRef.current = JSON.stringify(changes);

      // Convert profile to array of segments
      const currentSegments = [
        petProfile.selectedAge,
        ...(petProfile.selectedNeeds || []),
        ...(petProfile.selectedAllergies || []),
        ...(!isCatProfile(petProfile) ? [petProfile.selectedBreed] : []),
      ].filter(
        (segment): segment is AllSegmentationTypes => segment !== undefined
      );

      // If pet type has changed, we're switching profiles
      const isTypeSwitch = previousTypeRef.current !== petProfile.petType;

      if (isTypeSwitch) {
        // On type switch, combine current pet profile segments with user segments
        const newValue: SegmentationStore = {
          ageSegmentation:
            (petProfile.selectedAge &&
              activeType === 'cat' &&
              isCatSegmentation(petProfile.selectedAge)) ||
            (petProfile.selectedAge &&
              activeType === 'dog' &&
              !isCatSegmentation(petProfile.selectedAge))
              ? petProfile.selectedAge
              : undefined,
          breedSegmentation: isCatProfile(petProfile)
            ? undefined
            : petProfile.selectedBreed,
          needSegmentation: [
            ...(petProfile.selectedNeeds || []),
            ...userSegments
              .filter(
                (segment) =>
                  (activeType === 'cat' && isCatSegmentation(segment)) ||
                  (activeType === 'dog' && !isCatSegmentation(segment))
              )
              .filter(isNutritionSegmentation),
          ],
          allergySegmentation: [
            ...(petProfile.selectedAllergies || []),
            ...userSegments
              .filter(
                (segment) =>
                  (activeType === 'cat' && isCatAllergySegmentation(segment)) ||
                  (activeType === 'dog' && !isCatAllergySegmentation(segment))
              )
              .filter(isAllergySegmentation),
          ],
          lastSegmentation: multipleSegmentation?.lastSegmentation,
        };

        // Push combined segmentation without triggering user action
        pushSegmentation({
          segmentation: newValue,
          isUserAction: false,
        });
      } else {
        // Find removed segments (only when not switching types)
        const removedSegments = previousSegmentsRef.current.filter(
          (segment) => !currentSegments.includes(segment)
        );

        // Handle removed segments
        removedSegments.forEach((segment) => {
          // Only call removeSegment when it's not a type switch
          removeSegment(segment);
          removeSegmentation({ segmentation: segment });
        });

        // Handle new segments
        currentSegments.forEach((segment) => {
          if (!previousSegmentsRef.current.includes(segment)) {
            pushSegmentation({ segmentation: segment });
          }
        });
      }

      // Update refs for next comparison
      previousSegmentsRef.current = currentSegments;
      previousTypeRef.current = petProfile.petType;
    } catch (error) {
      console.error('Error syncing segmentation', error);
    }
  }, [
    petProfile,
    multipleSegmentation,
    pushSegmentation,
    removeSegment,
    activeType,
    changes,
    userSegments,
    removeSegmentation,
  ]);
};

export default useSyncSegmentation;
